import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api } from "./api";
import { useAuth } from "./AuthContext";

const SavedContext = createContext(null);
const STORAGE_KEY = "okwatch:saved_locations:v1";

function load() {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : [];
  } catch (e) {
    // Private-mode browsers throw on localStorage access; safe to fall back to empty list.
    console.warn("SavedLocations: localStorage read failed", e);
    return [];
  }
}

function persist(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    // QuotaExceeded or private-mode write block — non-fatal, just log it.
    console.warn("SavedLocations: localStorage write failed", e);
  }
}

export function SavedLocationsProvider({ children }) {
  const [locations, setLocations] = useState(load);
  const { user } = useAuth();
  const [syncState, setSyncState] = useState({ status: "idle", lastSyncAt: null });
  // skipNextSync prevents the post-adopt effect from immediately re-PUTing
  // the server list we just received.
  const skipNextSync = useRef(false);
  const hasMerged = useRef(false);

  useEffect(() => { persist(locations); }, [locations]);

  // When user logs in: this device's list is authoritative. Only adopt the
  // server list when the device has none — union-merging resurrected
  // deleted/edited locations ("locations come right back").
  useEffect(() => {
    if (!user || user === false) {
      hasMerged.current = false;
      setSyncState({ status: "idle", lastSyncAt: null });
      return;
    }
    if (hasMerged.current) return;
    hasMerged.current = true;
    (async () => {
      setSyncState((s) => ({ ...s, status: "syncing" }));
      try {
        const { data } = await api.get("/saved-locations");
        const remote = data?.locations || [];
        if (locations.length === 0 && remote.length > 0) {
          skipNextSync.current = true;
          setLocations(remote);
        } else if (locations.length > 0) {
          skipNextSync.current = true;
          await api.put("/saved-locations", { locations });
        }
        setSyncState({ status: "synced", lastSyncAt: new Date().toISOString() });
      } catch (e) {
        // 401 means token expired — fall through, will re-sync on next login
        console.warn("SavedLocations: server fetch failed", e?.response?.status);
        setSyncState({ status: "error", lastSyncAt: null });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // When locations change while logged in: PUT to server (debounced via skip flag)
  useEffect(() => {
    if (!user || user === false) return;
    if (skipNextSync.current) {
      skipNextSync.current = false;
      return;
    }
    const t = setTimeout(() => {
      setSyncState((s) => ({ ...s, status: "syncing" }));
      api.put("/saved-locations", { locations })
        .then(() => setSyncState({ status: "synced", lastSyncAt: new Date().toISOString() }))
        .catch((e) => {
          console.warn("SavedLocations: server sync failed", e?.response?.status);
          setSyncState({ status: "error", lastSyncAt: null });
        });
    }, 600);
    return () => clearTimeout(t);
  }, [locations, user]);

  const add = useCallback((loc) => {
    setLocations((prev) => {
      if (prev.some((p) => Math.abs(p.lat - loc.lat) < 1e-4 && Math.abs(p.lon - loc.lon) < 1e-4)) {
        return prev;
      }
      const item = {
        id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        label: loc.label || "Location",
        lat: loc.lat,
        lon: loc.lon,
        radius_mi: loc.radius_mi || 10,
        created_at: new Date().toISOString(),
      };
      return [...prev, item];
    });
  }, []);

  const remove = useCallback((id) => {
    setLocations((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const update = useCallback((id, patch) => {
    setLocations((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  const clear = useCallback(() => setLocations([]), []);

  // Explicit save: PUT to server (when logged in) and report the result.
  const saveNow = useCallback(async () => {
    if (!user || user === false) return true;
    setSyncState((s) => ({ ...s, status: "syncing" }));
    try {
      await api.put("/saved-locations", { locations });
      setSyncState({ status: "synced", lastSyncAt: new Date().toISOString() });
      return true;
    } catch (e) {
      console.warn("SavedLocations: explicit save failed", e?.response?.status);
      setSyncState({ status: "error", lastSyncAt: null });
      return false;
    }
  }, [locations, user]);

  const value = useMemo(
    () => ({ locations, add, remove, update, clear, saveNow, syncState }),
    [locations, add, remove, update, clear, saveNow, syncState],
  );

  return <SavedContext.Provider value={value}>{children}</SavedContext.Provider>;
}

export function useSavedLocations() {
  const ctx = useContext(SavedContext);
  if (!ctx) return {
    locations: [], add: () => {}, remove: () => {}, update: () => {}, clear: () => {},
    saveNow: async () => true,
    syncState: { status: "idle", lastSyncAt: null },
  };
  return ctx;
}
